import { z } from 'zod';

export const NOTES_MAX_LENGTH = 500;
export const ASSIST_CONTEXT_MAX_LENGTH = 2000;

export const BRAND_ASSIST_SECTIONS = ['voice', 'mood', 'customer', 'dontDo'] as const;
export type BrandAssistSection = (typeof BRAND_ASSIST_SECTIONS)[number];

// ─── Voice ───
export const BrandVoiceSchema = z
  .object({
    /** 0 = formal, 10 = playful. Default 5. */
    formality: z.number().min(0).max(10),
    /** 0 = minimalist, 10 = maximalist. Default 5. */
    density: z.number().min(0).max(10),
    /** Up to 5 chips, each ≤ 24 chars. */
    threeWords: z.array(z.string().min(1).max(24)).max(5),
  })
  .strict();
export type BrandVoice = z.infer<typeof BrandVoiceSchema>;

// ─── Visual mood ───
export const BrandVisualMoodSchema = z
  .object({
    /** Up to 5 lowercase hex strings (with leading `#`). */
    palette: z.array(z.string().regex(/^#[0-9a-f]{3,8}$/i)).max(5),
    /** Up to 5 free-text descriptors / mood-board labels. */
    moodBoard: z.array(z.string().min(1).max(64)).max(5),
  })
  .strict();
export type BrandVisualMood = z.infer<typeof BrandVisualMoodSchema>;

// ─── Customer ───
export const BrandCustomerSchema = z
  .object({
    description: z.string().max(240),
    aspirations: z.array(z.string().min(1).max(32)).max(5),
  })
  .strict();
export type BrandCustomer = z.infer<typeof BrandCustomerSchema>;

// ─── Don't-do ───
export const BrandDontDoSchema = z
  .object({
    bannedWords: z.array(z.string().min(1).max(24)).max(8),
    bannedColours: z.array(z.string().min(1).max(24)).max(8),
    bannedStyles: z.array(z.string().min(1).max(24)).max(8),
  })
  .strict();
export type BrandDontDo = z.infer<typeof BrandDontDoSchema>;

// ─── Full BrandDna (response shape; server owns version + updatedAt) ───
export interface BrandDna {
  version: 1;
  voice: BrandVoice;
  visualMood: BrandVisualMood;
  customer: BrandCustomer;
  dontDo: BrandDontDo;
  notes: string;
  /** ISO 8601 — last server-side update timestamp. */
  updatedAt: string;
}

export const BRAND_DNA_DEFAULT: BrandDna = {
  version: 1,
  voice: { formality: 5, density: 5, threeWords: [] },
  visualMood: { palette: [], moodBoard: [] },
  customer: { description: '', aspirations: [] },
  dontDo: { bannedWords: [], bannedColours: [], bannedStyles: [] },
  notes: '',
  updatedAt: '',
};

// ─── PATCH /api/brand-dna body ───
/**
 * Patchable subset. Each top-level section is fully replaced (not deep-merged).
 * Sending no key leaves it untouched. The server controls `version` and `updatedAt`.
 */
export const BrandDnaPatchSchema = z
  .object({
    voice: BrandVoiceSchema.optional(),
    visualMood: BrandVisualMoodSchema.optional(),
    customer: BrandCustomerSchema.optional(),
    dontDo: BrandDontDoSchema.optional(),
    notes: z.string().max(NOTES_MAX_LENGTH).optional(),
  })
  .strict();
export type BrandDnaPatch = z.infer<typeof BrandDnaPatchSchema>;

// ─── POST /api/brand-dna/assist ───
export const BrandAssistRequestSchema = z
  .object({
    section: z.enum(BRAND_ASSIST_SECTIONS),
    /** Free text describing the brand. */
    context: z.string().min(1).max(ASSIST_CONTEXT_MAX_LENGTH),
  })
  .strict();
export type BrandAssistRequest = z.infer<typeof BrandAssistRequestSchema>;

/**
 * Wire response for `POST /api/brand-dna/assist`. `fields` is the suggested
 * patch for the requested section, shaped exactly like the matching slice of
 * `BrandDna`. `confidence` is a coarse [0, 1] score the UI may use to dim or
 * annotate uncertain results.
 */
export interface BrandAssistResponse {
  fields: Record<string, unknown>;
  confidence: number;
}
